import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { serialize } from "object-to-formdata";

import FormikTextField from "components/InputsField/formikInputs/FormikTextField";
import PhoneField from "components/InputsField/formikInputs/PhoneField";
import LogotypeInput from "components/InputsField/LogotypeInput";
import FormikLogotypeInput from "components/InputsField/formikInputs/FormikLogotypeInput";
import FormikMessengers from "components/InputsField/formikInputs/FormikMessengers";
import { MessengerDropdownService } from "../../../service/dropdownServices/MessengerDropdownService";
import Popup from "components/common/Popup";
import FormRow from "components/common/Layout/FormRow";
import Button from "components/common/Button";
import { useHttp } from "hooks/http.hook";
import { validation } from "./validate";
import { showErrorMessage, showSuccessMessage } from "helpers/notifications";
import { EMPLOYEE, EMPLOYEE_ENDPOINTS } from "endpointsConst";

import styles from "./styles.module.scss";

const EditEmployer = ({ employee, onClose, onSubmit, companyId }) => {
    const { t } = useTranslation();
    const lang = useSelector((state) => state.settings.lang);

    const [messengers, setMessengers] = useState([]);
    const { request, loading } = useHttp();

    const isCreateMode = employee.id ? false : true;

    const loadMessengers = useCallback(() => {
        MessengerDropdownService.getMessengers().then((res) => {
            setMessengers(res.data);
        });
    }, []);

    const handleChangeLogotype = useCallback(
        (file) => {
            const formData = new FormData();
            formData.append("picture", file ? file.file : null);
            formData.append("user_id", employee.id);
            formData.append("company_id", companyId);

            request(EMPLOYEE_ENDPOINTS.PHOTO, "patch", formData).then((res) => {
                onSubmit(employee.id, {
                    picture: res.data.url,
                });
            });
        },
        [request, employee.id, onSubmit, companyId],
    );

    const handleSubmit = useCallback(
        (values, { setErrors }) => {
            let userData = values;
            userData.company_id = companyId;

            if (isCreateMode) {
                const picture = values.picture ? values.picture : null;
                values.lang = lang;
                const valuesForSerialization = { ...values };
                if (picture) {
                    valuesForSerialization.picture = picture.file;
                }
                userData = serialize(valuesForSerialization, {
                  indices: true,
                  booleansAsIntegers: true,
                });
            } else {
                values.id = employee.id;
                values.messengers =
                    (values.messengers &&
                        values.messengers.reduce((acc, item) => {
                            if (item.messenger && item.messenger.length > 0) {
                                return [...acc, item];
                            }
                            return acc;
                        }, [])) ||
                    [];
            }

            request(EMPLOYEE + "/", isCreateMode ? "post" : "patch", userData)
                .then((res) => {
                    const data = isCreateMode ? { ...values, picture: res.data.picture } : values;
                    onSubmit &&
                        onSubmit(employee.id, {
                            id: res.data.id,
                            ...data,
                        });
                    if (isCreateMode) {
                        showSuccessMessage(t("employee.add_employee.employeeCreatedMessage", { email: values.email }));
                    }
                    onClose && onClose();
                })
                .catch((e) => {
                    const errors = {};
                    e.response.data.errors.forEach((error) => {
                        errors[error.path] = t(`${error.message}`);
                    });
                    setErrors(errors);
                });
        },
        [employee.id, onClose, onSubmit, request, isCreateMode, companyId, t, lang],
    );

    const handleSetError = useCallback((error) => {
        showErrorMessage(error);
    }, []);

    useEffect(() => {
        loadMessengers();
    }, [loadMessengers]);

    const initialValues = useMemo(
        () => ({
            firstname: employee.firstname || "",
            middlename: employee.middlename || "",
            lastname: employee.lastname || "",
            position: employee.position || "",
            email: employee.email || "",
            mobile_phone: employee.mobile_phone || "",
            messengers: employee.messengers || "",
        }),
        [employee],
    );

    return (
        <Popup
            title={isCreateMode ? t("employee.add_employee.create") : t("employee.edit")}
            onClose={onClose}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validation(t)}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ setFieldTouched }) => (
                    <Form>
                        <FormRow withSpace>
                            {isCreateMode ? (
                                <FormikLogotypeInput
                                    name="picture"
                                    label={t("employee.add_employee.add_photo_box")}
                                    wrapperClassName={styles.photo}
                                />
                            ) : (
                                <LogotypeInput
                                    wrapperClassName={styles.photo}
                                    onChange={handleChangeLogotype}
                                    name="picture"
                                    uploadedFile={
                                        employee.picture
                                            ? {
                                                  preview: employee.picture,
                                              }
                                            : null
                                    }
                                    label={t("employee.add_employee.add_photo_box")}
                                    setError={handleSetError}
                                />
                            )}
                            <div>
                                <FormikTextField
                                    name="lastname"
                                    maxLength="32"
                                    label={t("employee.add_employee.lastname")}
                                    wrapperClassName={styles["field--with-margin"]}
                                    required
                                />
                                <FormikTextField
                                    name="firstname"
                                    maxLength="32"
                                    label={t("employee.add_employee.firstname")}
                                    wrapperClassName={styles["field--with-margin"]}
                                    required
                                />
                                <FormikTextField
                                    name="middlename"
                                    maxLength="32"
                                    label={t("employee.add_employee.middlename")}
                                    wrapperClassName={styles["field--with-margin"]}
                                />
                            </div>
                        </FormRow>
                        <FormRow withSpace>
                            <FormikTextField
                                name="email"
                                maxLength="64"
                                label={t("employee.add_employee.email")}
                                wrapperClassName={styles["field--with-margin"]}
                                disabled={!isCreateMode}
                                required
                            />
                            <PhoneField
                                wrapperClass={styles["field--with-margin"]}
                                specialLabel=""
                                onTouch={setFieldTouched}
                                label={t("employee.add_employee.phone")}
                                name="mobile_phone"
                                required
                            />
                        </FormRow>
                        <FormRow withSpace>
                            <FormikTextField
                                name="position"
                                maxLength="64"
                                label={t("employee.add_employee.position")}
                                wrapperClassName={styles["field--with-margin"]}
                            />
                            <></>
                        </FormRow>
                        <FormikMessengers
                            label={t("employee.add_employee.addMessenger")}
                            messengersList={messengers}
                            name="messengers"
                        />
                        <div className={styles.button}>
                            <Button disable={loading.toString()} type="submit">
                                {t("employee.add_employee.save_button")}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Popup>
    );
};

export default EditEmployer;
